/** Display-only query snapshots. Never cache assessment, revision or editor reads. */
export function cacheable(path: string): boolean {
  return /^\/api\/(?:home(?:\/statistics)?|workouts\/(?:summary|history)|nutrition\/day|arena|exercises|gyms\/[^/?]+|me\/photo\/status)(?:\?[^#]*)?$/.test(path);
}
type Entry = { value?: unknown; updated: number; dirty: boolean; version: number; bytes: number; flight?: Promise<unknown> };
export class NavigationCache {
  private active = 0;
  private entries = new Map<string, Entry>();
  private listeners = new Set<() => void>();
  private owner = ''; private timezone = 'UTC'; private day = ''; private epoch = 0;
  constructor(private now = () => Date.now(), readonly limit = 48, readonly byteLimit = 4 * 1024 * 1024, readonly ttl = 30_000) {}
  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  private emit() { for (const fn of this.listeners) fn(); }
  clear() { this.epoch++; this.entries.clear(); this.emit(); }
  context(owner: string, timezone: string) {
    if (owner !== this.owner || timezone !== this.timezone) {
      this.owner = owner; this.timezone = timezone; this.day = ''; this.clear();
    }
    this.tick();
  }
  tick() {
    const day = new Intl.DateTimeFormat('en-CA', { timeZone: this.timezone, year:'numeric', month:'2-digit', day:'2-digit' }).format(this.now());
    if (day !== this.day) { this.day = day; this.clear(); }
  }
  peek<T>(path: string): T | null {
    this.tick(); const e = this.entries.get(path);
    if (!e) return null;
    this.entries.delete(path); this.entries.set(path, e);
    return (e.value as T) ?? null;
  }
  invalidate() {
    for (const e of this.entries.values()) { e.dirty = true; e.version++; e.flight = undefined; }
    this.emit();
  }
  stats() { return { entries: this.entries.size, bytes: [...this.entries.values()].reduce((n,e)=>n+e.bytes,0) }; }
  private bound() {
    while (this.entries.size > this.limit || this.stats().bytes > this.byteLimit) this.entries.delete(this.entries.keys().next().value!);
  }
  async read<T>(path: string, load: () => Promise<T>, force = false): Promise<T> {
    this.tick();
    if (!cacheable(path) || !this.owner) return load();
    let e = this.entries.get(path);
    if (!e) { e = { updated: 0, dirty: true, version: 0, bytes: 0 }; this.entries.set(path,e); this.bound(); }
    if (e.flight) return e.flight as Promise<T>;
    if (!force && !e.dirty && e.value !== undefined && this.now()-e.updated < this.ttl) return e.value as T;
    if (this.active >= 12) throw new Error('Загрузка занята. Повторите попытку');
    this.active++;
    const entry = e, epoch = this.epoch, version = e.version;
    const flight = Promise.resolve().then(load).then(value => {
      this.tick();
      if (epoch !== this.epoch || entry.version !== version) throw new Error('Данные обновились. Повторите загрузку');
      if (this.entries.get(path) === entry) {
        entry.value = value; entry.updated = this.now(); entry.dirty = false;
        entry.bytes = JSON.stringify(value)?.length * 2 || 0; this.bound();
      }
      return value;
    }).finally(()=>{this.active--; if(entry.flight===flight)entry.flight=undefined;});
    entry.flight = flight;
    return flight;
  }
}
export const navigationCache = new NavigationCache();
// This artifact is built explicitly for the dev host; default/prod builds stay OFF.
export const navigationCacheEnabled = import.meta.env?.VITE_NAVIGATION_CACHE === '1';
